import { GLOBAL } from 'saddlebag-browser';
import { logError, logWarn } from 'saddlebag-logger';

import {
  getSourceDomain,
  isInternalError,
  isInternalSourceError,
  logUnhandled,
} from './utils';

const UN_HANDLED_REJECTION_COMPONENT = 'windowOnUnhandledRejection';
const ON_ERROR_COMPONENT = 'windowOnError';
const LOG_SEND_THRESHOLD = 100;

type ErrorLike = {
  message?: unknown;
  stack?: unknown;
};

type LogPayload = {
  component: string;
  message: string;
  source?: string;
  error?: unknown;
};

const $window = GLOBAL.getWindow();

let sentErrorCount = 0;
let errorMessages: string[] = [];

const isErrorLike = (value: unknown): value is ErrorLike =>
  typeof value === 'object' && value !== null;

const getErrorStack = (error: unknown): string | undefined => {
  if (!isErrorLike(error)) {
    return undefined;
  }

  return typeof error.stack === 'string' ? error.stack : undefined;
};

const log = ({ component, error, message, source }: LogPayload): void => {
  const safeMessage = message || 'Unknown error';

  const isNewError = !errorMessages.includes(safeMessage);
  const isBelowThreshold = sentErrorCount < LOG_SEND_THRESHOLD;

  if (!isNewError || !isBelowThreshold) {
    return;
  }

  const data: Record<string, unknown> = {
    component,
    message: safeMessage,
  };

  if (source) {
    data.source = source;
  }

  if (error !== undefined && error !== null) {
    data.error = error;

    const stack = getErrorStack(error);
    if (stack) {
      data.stack = stack;
    }
  }

  const errorObj = error instanceof Error ? error : new Error(safeMessage);
  logError(errorObj, data);
  sentErrorCount += 1;
  errorMessages.push(safeMessage);
};

export const newRelicErrorHandler = (error: unknown): boolean => {
  if (typeof error === 'string') {
    return false;
  }

  if (!isErrorLike(error)) {
    return false;
  }

  const message = typeof error.message === 'string' ? error.message : '';
  const stack = typeof error.stack === 'string' ? error.stack : '';

  try {
    const sourceDomain = getSourceDomain(stack);

    if (!isInternalError({ message, sourceDomain })) {
      return true;
    }
  } catch {
    logWarn({
      component: 'newRelicErrorHandler',
      message: 'Failed to parse an error in newRelicErrorHandler',
      stack,
    });
  }
  return false;
};

type OnErrorArgs = [
  messageEvent: string | Event,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error,
];

const getErrorMessage = (messageEvent: string | Event, error?: unknown) => {
  if (typeof messageEvent === 'string' && messageEvent) {
    return messageEvent;
  }

  if (isErrorLike(error) && typeof error.message === 'string') {
    return error.message;
  }

  if (typeof messageEvent === 'object' && messageEvent) {
    return messageEvent.toString();
  }

  return 'Unknown error';
};

export const setupErrorHandling = (): void => {
  sentErrorCount = 0;
  errorMessages = [];

  if ($window.newrelic?.setErrorHandler) {
    /*
    https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/seterrorhandler/
    https://github.com/newrelic/newrelic-browser-agent/blob/129f3b089170079b3970dfe58cbc01564ccabd98/src/loaders/agent-base.js#L71

    By default since v1220 New Relic adds its own onerror and onunhandledrejection handlers. It provides the `setErrorHandler` method to allow errors to be ignored.
    If the handler returns true, New Relic does not record the error.

    IMPORTANT: This error handler is called for _all_ errors, not just unhandled ones, so cannot simply be turned off; doing so would stop our logError from working.
    */
    $window.newrelic.setErrorHandler(newRelicErrorHandler);
  }

  $window.onerror = ((...args: OnErrorArgs) => {
    /*
    https://developer.mozilla.org/en-US/docs/Web/API/Window/error_event

    window.onerror always includes the source and message. We use this to filter our errors believed to come from code outside our application, as this is beyond our control so unactionable.

    However, as the New Relic Browser Agent intercepts errors and so adds a 'nrWrapper' entry as the final frame on many stack traces, which 'source' is determined from by browsers, we cannot trust this source property, so must parse it ourselves using 'getSourceDomain'.
    */

    const [messageEvent, , , , error] = args;
    const stack = getErrorStack(error) ?? '';
    const message = getErrorMessage(messageEvent, error);

    try {
      const sourceDomain = getSourceDomain(stack);

      // Don't log cross-domain script errors
      // Don't log errors which don't come from our scripts
      if (isInternalError({ message, sourceDomain })) {
        log({
          component: ON_ERROR_COMPONENT,
          message,
          error,
          source: sourceDomain,
        });
      }
    } catch {
      logWarn({
        component: ON_ERROR_COMPONENT,
        message: 'Error evaluating an error in window.onerror',
        stack,
      });
    }
  }) as OnErrorEventHandler;

  $window.onunhandledrejection = (rejection: PromiseRejectionEvent) => {
    const { reason } = rejection;

    if (!reason) {
      logUnhandled({
        component: UN_HANDLED_REJECTION_COMPONENT,
        message: 'Unhandled promise rejection with undefined rejection reason',
        source: 'undefined source',
      });
      return;
    }

    try {
      const sourceDomain = getSourceDomain(
        getErrorStack(reason) ?? String(reason ?? ''),
      );

      // TODO: WOODPECKER-2226 Add more fields for chunks failed laoding issue
      if (isInternalSourceError(sourceDomain)) {
        log({
          component: UN_HANDLED_REJECTION_COMPONENT,
          error: reason,
          message:
            isErrorLike(reason) && typeof reason.message === 'string'
              ? reason.message
              : 'Unhandled promise rejection with internal source',
          source: sourceDomain,
        });
        return;
      }

      // we segment into a separate table, as with `!rejection.reason` segmentation
      logUnhandled({
        component: UN_HANDLED_REJECTION_COMPONENT,
        message: 'Unhandled promise rejection with third party source',
        stack: getErrorStack(reason),
        source: sourceDomain || 'undefined source',
      });
    } catch {
      const reasonStack = getErrorStack(reason);
      logWarn({
        component: UN_HANDLED_REJECTION_COMPONENT,
        message: 'Unhandled promise rejection with undetermined source',
        ...(reasonStack != null && { stack: reasonStack }),
      });
    }
  };
};
